import { useEffect, useState } from 'react';
import type { PayPalCheckout } from 'braintree-web/paypal-checkout';
import { ButtonColorOption, ButtonLabelOption, ButtonShapeOption, ButtonSizeOption } from '../../common/types';
import type {
  AuthorizationData,
  AuthorizationResponse,
  ButtonStyle,
  PaypalButtonConfig,
  PaypalCallbacks,
  PaypalInstanceType,
} from '../../common/types';
import { initiatePaypalSDK } from '../../controller/paypal-button/initiate-paypal-sdk';

export const usePaypalSDK = ({
  parentElementId,
  callbacks: { onPaypalApprove, onPaypalButtonRender, onPaypalCancel, onPaypalError, onPaypalClick },
  config,
}: {
  parentElementId: string;
  callbacks: PaypalCallbacks;
  config?: PaypalButtonConfig;
}): PaypalInstanceType => {
  const [paypalInstance, setPaypalInstance] = useState<PayPalCheckout | null>(null);
  const [isPaypalButtonRendered, setButtonRendered] = useState<boolean>(false);
  const [isPaypalLoadingError, setPaypalLoadingError] = useState<boolean>(false);

  useEffect(() => {
    const init = (): void => {
      initiatePaypalSDK()
        .then((paypalCheckoutInstance) => {
          setPaypalInstance(paypalCheckoutInstance);

          return window.paypal
            .Buttons({
              fundingSource: 'paypal',
              style: {
                label: ButtonLabelOption.PayPal,
                size: ButtonSizeOption.Responsive, // min-width: 15px, max-width: 500px
                shape: ButtonShapeOption.Rect,
                color: ButtonColorOption.Gold,
                height: 40,
                tagline: false,
                ...(config?.style as ButtonStyle),
              },

              createBillingAgreement() {
                return paypalCheckoutInstance.createPayment({
                  displayName: config?.displayName || 'Atlassian',

                  flow: 'vault' as typeof window.paypal.FlowType.Vault,
                });
              },

              onApprove(data: AuthorizationData): Promise<AuthorizationResponse> {
                const tokenizePaymentMethod = paypalCheckoutInstance.tokenizePayment(data);

                tokenizePaymentMethod
                  .then((paypalPayload: paypal.TokenizePayload) => {
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    void onPaypalApprove?.({ paypalPayload });
                  })
                  .catch((error) => {
                    if (onPaypalError) {
                      onPaypalError(error);
                    }
                  });

                return tokenizePaymentMethod;
              },

              onCancel() {
                if (onPaypalCancel) {
                  onPaypalCancel();
                }
              },

              onError(error: string) {
                setPaypalLoadingError(true);

                if (onPaypalError) {
                  onPaypalError(error);
                }
              },

              onClick() {
                if (onPaypalClick) {
                  onPaypalClick();
                }
              },
            })
            .render(`#${parentElementId}`);
        })
        .then(() => {
          setButtonRendered(true);

          if (onPaypalButtonRender) {
            onPaypalButtonRender();
          }
        })
        .catch((error) => {
          setPaypalLoadingError(true);

          if (onPaypalError) {
            onPaypalError(error);
          }
        });
    };

    void init();
  }, [config, onPaypalApprove, onPaypalCancel, onPaypalClick, onPaypalError, parentElementId, onPaypalButtonRender]);

  return {
    paypalInstance,
    isPaypalButtonRendered,
    isPaypalLoadingError,
  };
};
